/**
 * Who a conversation message is for (conversation-members spec, owner 2026-10-29).
 *
 * Every participant — a person, a persona, an employee, an external agent connected over MCP — is a member of the
 * conversation, and its `conversation_members` row id is its ADDRESS there. A message names its author and its
 * recipients by address; only the members it is addressed to receive it, because each AI member that receives one
 * runs a model cycle. A message from a person or an external agent that names no one goes to the conversation's lead.
 *
 * The client sends the addresses it picked (`to` on the reply), never text for anyone to search, and the server checks
 * each is an active member before the message is stored.
 */
import { z } from 'zod';

export const CONVERSATION_MEMBER_TYPES = [
  'user',
  'persona',
  'workforce_member',
  'external_agent',
  'agent_instance',
] as const;
export type ConversationMemberType = (typeof CONVERSATION_MEMBER_TYPES)[number];

export const CONVERSATION_MEMBER_ROLES = ['owner', 'contributor', 'observer', 'lead'] as const;
export type ConversationMemberRole = (typeof CONVERSATION_MEMBER_ROLES)[number];

/** The member types the engine runs when a message is delivered to them. */
export const AI_MEMBER_TYPES = ['persona', 'workforce_member'] as const satisfies readonly ConversationMemberType[];
export type AiMemberType = (typeof AI_MEMBER_TYPES)[number];

export function isAiMemberType(type: ConversationMemberType): type is AiMemberType {
  return (AI_MEMBER_TYPES as readonly ConversationMemberType[]).includes(type);
}

/** At most this many AI members receive one message: each runs a model cycle. */
export const MAX_AI_RECIPIENTS = 3;

/** At most this many members, people included, are named on one message. */
export const MAX_RECIPIENTS = 20;

/**
 * How deep a chain of asks may go: the links from a message back to the person's message that started the work
 * (`serving_message_id`, else `reply_to_message_id`). A message this deep is not run: its member says why instead. Six
 * lets a lead consult two specialists in turn and still stops two members asking each other back and forth.
 */
export const MAX_ASK_DEPTH = 6;

/** A member as a message names it: its address (`id`) and who it is. */
export const ConversationMemberRefSchema = z.object({
  /** The member's address in the conversation (`conversation_members.id`). */
  id: z.number().int().positive(),
  memberType: z.enum(CONVERSATION_MEMBER_TYPES),
  /** What the member is (`conversation_members.member_id`): a user, an entity, or an API key. */
  memberId: z.number().int().positive(),
  name: z.string().min(1).max(200),
  role: z.enum(CONVERSATION_MEMBER_ROLES),
});
export type ConversationMemberRef = z.infer<typeof ConversationMemberRefSchema>;

/**
 * Where a message stands for one of its recipients, read from the messages and the members (no table of its own):
 * `running` — the member is working on it; `pending` — it has not dealt with it yet; `answered` — it answered (or, an AI
 * member, carried the work on); `read` — a person or an external agent dealt with it without answering it; `failed` —
 * the member said why it did not answer.
 */
export const RECIPIENT_STATUSES = ['pending', 'running', 'answered', 'read', 'failed'] as const;
export type RecipientStatus = (typeof RECIPIENT_STATUSES)[number];

/** One member a message is for, and where it stands for them. */
export const ConversationRecipientSchema = ConversationMemberRefSchema.extend({
  status: z.enum(RECIPIENT_STATUSES),
  /** The message asks this member something and waits on their reply. */
  replyNeeded: z.boolean(),
  /** Why the member did not answer. */
  reason: z.string().nullable(),
});
export type ConversationRecipient = z.infer<typeof ConversationRecipientSchema>;

/** The addresses a message is for. */
export const ConversationRecipientsSchema = z.array(z.number().int().positive()).max(MAX_RECIPIENTS);

/** Each address once, in the order it was named. */
export function distinctRecipients(recipients: readonly number[]): number[] {
  return [...new Set(recipients)];
}

/** The word the conversation shows beside a member's name. */
export function memberKindLabel(type: ConversationMemberType): string {
  switch (type) {
    case 'user':
      return 'Person';
    case 'persona':
      return 'Persona';
    case 'workforce_member':
      return 'Employee';
    case 'external_agent':
      return 'Agent';
    case 'agent_instance':
      return 'Agent';
  }
}
